import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@tellescope/ui";
import { Button } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useInboxTableViews } from "../../../../hooks";
import { countActiveInboxFilters, inboxFiltersAreEqual, type InboxFilters } from "../../utils";
import { InboxFilter } from "./InboxFilters";

export interface InboxFilterDialogProps {
  open?: boolean;
  setOpen?: (open: boolean) => void;
}

export const InboxFilterDialog = ({ open = false, setOpen }: InboxFilterDialogProps) => {
  const { t } = useTranslation("inbox");
  const { effectiveFilters, applyFilters, resetStagedFilters } = useInboxTableViews();

  const [stagedFilters, setStagedFilters] = useState<InboxFilters>(effectiveFilters);
  // Re-seed on open so edits abandoned by Cancel don't leak into the next open.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setStagedFilters(effectiveFilters);
  }

  const isDirty = !inboxFiltersAreEqual(stagedFilters, effectiveFilters);
  const activeFilterCount = countActiveInboxFilters(stagedFilters);

  const handleApply = () => {
    applyFilters(stagedFilters);
    setOpen?.(false);
  };

  const handleClearAll = () => {
    setStagedFilters({});
    resetStagedFilters();
    setOpen?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex max-h-[85vh] min-h-[600px] w-[860px] max-w-[95vw] flex-col p-0" divided>
        <DialogHeader className="flex-shrink-0 border-b border-b-gray-200 p-4">
          <DialogTitle className="text-sm font-normal">{t("Filter Messages")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("Apply filters to narrow down your thread list")}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
          <InboxFilter stagedFilters={stagedFilters} onStagedFiltersChange={setStagedFilters} />
        </div>
        <DialogFooter className="flex gap-2 bg-muted justify-between sm:justify-between">
          <Button
            variant="link"
            onClick={handleClearAll}
            disabled={activeFilterCount === 0}
            data-testid="inbox-filter-clear-all"
            size="sm"
          >
            {t("Clear All")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen?.(false)} data-testid="inbox-filter-cancel" size="sm">
              {t("Cancel")}
            </Button>
            <Button onClick={handleApply} disabled={!isDirty} data-testid="inbox-filter-apply" size="sm">
              {t("Apply Filters")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
